
import React, { useMemo, useState } from "react";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { useReportsUIContext } from "../ReportsUIContext";
import Select from 'react-select';
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import * as reportsActions from '../../_redux/reportsActions';
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { OrderArticleLotReportToCSV } from "./OrderArticleLotReportToCSV";
import { errorMsg } from "../../../Global/helpers";

// Los lotes se le piden a RIGA-FARMA pedido por pedido: cuanto más largo el rango, más
// pedidos hay que consultar. Por eso el período es obligatorio y acotado (mismo tope que
// valida el back, que además corta por cantidad de pedidos).
const MAX_DIAS_RANGO = 7;


const prepareFilter = (queryParams, values) => {
  const { zone_id, shipping_type, order_creation_from , order_creation_to, dispatched_date_from, dispatched_date_to, business_unit_id } = values;

  const newQueryParams = { ...queryParams, filter:{...queryParams.filter} };

  if(zone_id && zone_id !== ""){
    newQueryParams.filter.zone_id = zone_id;
  }
  else{
    newQueryParams.filter.zone_id = undefined;
  }

  // Filter by shipping_type
  if(shipping_type && shipping_type !== ""){
    newQueryParams.filter.shipping_type = shipping_type;
  }
  else{
    newQueryParams.filter.shipping_type = undefined;
  }

  if(order_creation_from  && order_creation_from  !== ""){
    newQueryParams.filter.order_creation_from  = moment(order_creation_from ).format("YYYYMMDD");
  }
  else{
    newQueryParams.filter.order_creation_from  = undefined;
  }

  if(order_creation_to && order_creation_to !== ""){
    newQueryParams.filter.order_creation_to = moment(order_creation_to).format("YYYYMMDD");
  }
  else{
    newQueryParams.filter.order_creation_to = undefined;
  }

  if(business_unit_id && business_unit_id !== ""){
    newQueryParams.filter.business_unit_id = business_unit_id;
  }
  else{
    newQueryParams.filter.business_unit_id = undefined;
  }

  return newQueryParams;
};

export function OrderArticleLotReportFilters({ listLoading }) {
// Arranca con la última semana ya elegida: es el uso habitual y evita que el usuario
// tenga que completar el rango a mano cada vez
const [createdFrom, setCreatedFrom] = useState(moment().subtract(MAX_DIAS_RANGO - 1, 'days').toDate());
const [createdTo, setCreatedTo] = useState(new Date());
const [despachoFrom, setDespachoFrom] = useState(null);
const [despachoTo, setDespachoTo] = useState(null);
const [newQueryParams, setNewQueryParams] = useState(null);
const reportsUIContext = useReportsUIContext();
const reportsUIProps = useMemo(() => {
  return {
    setActualReportQueryParams: reportsUIContext.setActualReportQueryParams,
    actualReportQueryParams: reportsUIContext.actualReportQueryParams,
  };
}, [reportsUIContext]);


const { entities, business_units } = useSelector(
    (state) => ({
      entities: state.reports.report.entities,
      business_units: state.helpers.BusinessUnits
    }),
    shallowEqual
);

const applyFilter = (values) => {
  setNewQueryParams(prepareFilter(reportsUIProps.actualReportQueryParams, values))
};

const zonasUI = [
  {value:1, label:'CABA'},
  {value:2, label:'GBA'},
  {value:3, label:'Interior'},
  {value:7, label:'Tierra del Fuego'}
]

const shipping_types = [
    {value:1, label:'Envío normal'},
    {value:2, label:'Envío express'},
    {value:3, label:'Retiro en Farmacia'},
    {value:4, label:'Punto Pickit'},
]

const dispatch = useDispatch();

// Tiene que venir completo al menos uno de los dos rangos y ninguno puede pasar del tope:
// cada día de rango son más pedidos que consultarle a RIGA-FARMA para resolver los lotes
const rangoValido=(desde, hasta, cual)=>{

    if(!desde && !hasta){
      return true;   // rango vacío: no se usa
    }

    if(!desde || !hasta){
      errorMsg(`Completá las dos fechas del rango ${cual}`);
      return false;
    }

    if(moment(hasta).startOf('day').isBefore(moment(desde).startOf('day'))){
      errorMsg(`En el rango ${cual}, la fecha "hasta" no puede ser anterior a la fecha "desde"`);
      return false;
    }

    const dias = moment(hasta).startOf('day').diff(moment(desde).startOf('day'), 'days') + 1;
    if(dias > MAX_DIAS_RANGO){
      errorMsg(`El rango ${cual} no puede superar los ${MAX_DIAS_RANGO} días (elegiste ${dias})`);
      return false;
    }

    return true;
}

const filtrosValidos=()=>{

    if(!createdFrom && !createdTo && !despachoFrom && !despachoTo){
      errorMsg(`Elegí un rango de fechas, de pedido o de despacho (máximo ${MAX_DIAS_RANGO} días)`);
      return false;
    }

    if(!rangoValido(createdFrom, createdTo, 'de pedido')){
      return false;
    }
    if(!rangoValido(despachoFrom, despachoTo, 'de despacho')){
      return false;
    }

    return true;
}

const executeReport=()=>{

    if(!filtrosValidos()){
      return;
    }

    // Las fechas se toman siempre de los selectores, así el rango viaja aunque el usuario
    // no haya tocado ningún otro filtro (arrancan con la última semana ya elegida)
    const base = newQueryParams || reportsUIProps.actualReportQueryParams;
    const params = {
      ...base,
      pageNumber: 1,
      filter: {
        ...base.filter,
        order_creation_from: createdFrom ? moment(createdFrom).format("YYYYMMDD") : undefined,
        order_creation_to: createdTo ? moment(createdTo).format("YYYYMMDD") : undefined,
        dispatched_date_from: despachoFrom ? moment(despachoFrom).format("YYYYMMDD") : undefined,
        dispatched_date_to: despachoTo ? moment(despachoTo).format("YYYYMMDD") : undefined,
      },
    };

    reportsUIProps.setActualReportQueryParams(params);
    dispatch(reportsActions.fetchReportsByName("order-articles-lot", params));

}

return (
  <>
    <Formik
      initialValues={{
        status: "", // values => All=""/Selling=0/Sold=1
        condition: "", // values => All=""/New=0/Used=1
        searchText: "",
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right" >

          <div className="form-group row">

              <div className="col-lg-3">
                  <ReactDatePicker
                      autoComplete='off'
                      className="form-control"
                      style={{ width: "100%" }}
                      name="createdFrom"
                      selected={createdFrom}
                      maxDate={createdTo?(createdTo>new Date()?new Date():createdTo):new Date()}
                      dateFormat="dd/MM/yyyy"
                      isClearable
                      onChange={(date) => {
                      setCreatedFrom(date);
                      setFieldValue("order_creation_from", date?date:null);
                      handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                Pedidos <b>desde</b>
                  </small>
              </div>

              <div className="col-lg-3">
                  <ReactDatePicker
                      autoComplete='off'
                      className="form-control"
                      style={{ width: "100%" }}
                      name="createdTo"
                      selected={createdTo}
                      minDate={createdFrom}
                      /* No se puede elegir un "hasta" que estire el rango más allá del tope */
                      maxDate={createdFrom
                        ? moment.min(moment(createdFrom).add(MAX_DIAS_RANGO - 1, 'days'), moment()).toDate()
                        : new Date()}
                      dateFormat="dd/MM/yyyy"
                      isClearable
                      onChange={(date) =>  {
                          setCreatedTo(date);
                          setFieldValue("order_creation_to", date?date:null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                  Pedidos <b>hasta</b>
                  </small>
              </div>

              <div className="col-lg-3">
                  <ReactDatePicker
                      autoComplete='off'
                      className="form-control"
                      style={{ width: "100%" }}
                      name="despachoFrom"
                      selected={despachoFrom}
                      maxDate={despachoTo?(despachoTo>new Date()?new Date():despachoTo):new Date()}
                      dateFormat="dd/MM/yyyy"
                      isClearable
                      onChange={(date) => {
                          setDespachoFrom(date);
                          setFieldValue("dispatched_date_from", date?date:null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                  Despacho <b>desde</b>
                  </small>
              </div>

              <div className="col-lg-3">
                  <ReactDatePicker
                      autoComplete='off'
                      className="form-control"
                      style={{ width: "100%" }}
                      name="despachoTo"
                      selected={despachoTo}
                      minDate={despachoFrom}
                      /* Mismo tope que en el rango de pedidos */
                      maxDate={despachoFrom
                        ? moment.min(moment(despachoFrom).add(MAX_DIAS_RANGO - 1, 'days'), moment()).toDate()
                        : new Date()}
                      dateFormat="dd/MM/yyyy"
                      isClearable
                      onChange={(date) => {
                          setDespachoTo(date);
                          setFieldValue("dispatched_date_to", date?date:null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                  Despacho <b>hasta</b>
                  </small>
              </div>

          </div>

          <div className="form-group row">

              <div className="col-lg-3">
                  <Select
                      options={zonasUI}
                      isMulti="true"
                      onChange={(e) => {
                          setFieldValue("zone_id", e?e.map(item=>item.value.toString()
                          .replace("2", "2,8,9,10")
                          .replace("3", "3,4,5,6,11,12")).join(','):null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                  <b>Zona</b>
                  </small>
              </div>

              <div className="col-lg-3">
                  <Select
                      options={business_units}
                      onChange={(e) => {
                          setFieldValue("business_unit_id", e?e.value:null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                    Filtro por <b>Unidad de Negocio</b>
                  </small>
              </div>

              <div className="col-lg-3">
                  <Select
                      options={shipping_types}
                      isMulti="true"
                      onChange={(e) => {
                          setFieldValue("shipping_type", e?e.map(item=>item.value).join(','):null);
                          handleSubmit();
                      }}
                  />
                  <small className="form-text text-muted">
                  <b>Tipo de Envío</b>
                  </small>
              </div>

              <div className="col float-right">

                  <button type="button" onClick={executeReport} className="btn btn-primary float-right m-2" >
                      <i className="fa fa-play"></i>
                      Ejecutar
                  </button>

                  { entities && entities.length>0 &&
                    <OrderArticleLotReportToCSV />
                  }

              </div>
          </div>

        </form>
      )}
    </Formik>
  </>
);
}
